import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { getSaleChannelName } from "./nhanhConfig";

// Format Unix timestamps for Excel export (Date only: DD/MM/YYYY)
const formatTimestamp = (ts) => {
  if (!ts) return "";
  const num = Number(ts);
  if (!Number.isNaN(num) && num > 0) {
    if (num < 100000000000) {
      return dayjs(num * 1000).format("DD/MM/YYYY");
    }
    return dayjs(num).format("DD/MM/YYYY");
  }
  if (
    typeof ts === "string" &&
    (ts.includes("-") || ts.includes("/") || ts.includes("T"))
  ) {
    const d = dayjs(ts);
    if (d.isValid()) return d.format("DD/MM/YYYY");
  }
  return String(ts);
};

// Format Unix timestamps for Excel export (Date only: YYMMDD)
const formatTimestampToYYMMDD = (ts) => {
  if (!ts) return "";
  const num = Number(ts);
  if (!Number.isNaN(num) && num > 0) {
    if (num < 100000000000) {
      return dayjs(num * 1000).format("YYMMDD");
    }
    return dayjs(num).format("YYMMDD");
  }
  if (
    typeof ts === "string" &&
    (ts.includes("-") || ts.includes("/") || ts.includes("T"))
  ) {
    const d = dayjs(ts);
    if (d.isValid()) return d.format("YYMMDD");
  }
  return String(ts);
};

// Format Expiry date (HSD) to DD/MM/YYYY
const formatHsd = (hsd) => {
  if (!hsd || hsd === "-") return "";
  const cleaned = String(hsd).trim();
  if (/^\d{8}$/.test(cleaned)) {
    return `${cleaned.substring(0, 2)}/${cleaned.substring(2, 4)}/${cleaned.substring(4)}`;
  }
  if (/^\d{6}$/.test(cleaned)) {
    return `${cleaned.substring(0, 2)}/${cleaned.substring(2, 4)}/20${cleaned.substring(4)}`;
  }
  return cleaned;
};

// Extract detailed products information for Excel export and reallocate combo prices
const parseOrderProducts = (raw, productsCatalogMap) => {
  if (!raw) return [];
  const prods = raw.products || raw.items || raw.productList || [];
  console.log("prods", prods);
  let rawItems = [];
  if (Array.isArray(prods)) {
    rawItems = prods;
  } else if (prods && typeof prods === "object") {
    rawItems = Object.values(prods);
  }

  if (rawItems.length === 0) {
    return [
      {
        name: "",
        sku: "",
        lot: "",
        hsd: "",
        imei: "",
        barcode: "",
        unit: "",
        category: "",
        weight: 0,
        originalPrice: 0,
        salePrice: 0,
        displaySalePrice: 0,
        priceDiff: 0,
        qty: 0,
        discountPerProduct: 0,
        totalDiscount: 0,
        priceAfterDiscount: 0,
      },
    ];
  }

  const parsedItems = [];

  rawItems.forEach((i) => {
    const rawName = String(i.productName || i.name || "").trim();
    const qty = Number(i.quantity || i.qty || 1);
    const price = Number(i.price || 0);
    console.log("price", price);
    const displaySalePrice = Number(
      i.displaySalePrice !== undefined && i.displaySalePrice !== null
        ? i.displaySalePrice
        : price,
    );
    const origPrice = Number(
      i.originalPrice || i.importPrice || i.listPrice || price,
    );
    const disc = Number(i.discount || 0);
    const w = Number(i.weight || 0);

    const itemCode = String(i.productCode || i.code || i.id || "").trim();
    const subItems = [];
    let isParsed = false;

    // Rule 1: Product code contains "CB" (case-insensitive)
    if (itemCode.toUpperCase().includes("CB")) {
      const parts = rawName.split(/\s+và\s+/i);
      if (parts.length > 0 && parts[0].includes("-")) {
        isParsed = true;
        parts.forEach((part) => {
          const trimmedPart = part.trim();
          if (!trimmedPart) return;

          const blocks = trimmedPart.split("-");
          console.log("blocks", blocks);
          if (blocks.length >= 3) {
            const firstBlock = blocks[0].trim();
            const pSku =
              firstBlock.substring(0, 2) + "MTP03010" + firstBlock.slice(-3);
            const pLot = blocks[1].trim();
            let pHsd = blocks[2].trim();

            let pQty = 1;
            if (blocks.length >= 4) {
              const qtyMatch = blocks[3].match(/^(\d+)/);
              if (qtyMatch) {
                pQty = parseInt(qtyMatch[0], 10);
              } else {
                const parenMatch = trimmedPart.match(/\(\s*(\d+)/);
                if (parenMatch) {
                  pQty = parseInt(parenMatch[1], 10);
                }
              }
            } else {
              const parenMatch = trimmedPart.match(/\(\s*(\d+)/);
              if (parenMatch) {
                pQty = parseInt(parenMatch[1], 10);
              }
            }

            const finalSubQty = pQty * qty;

            // Look up in productsCatalogMap
            const cleanSku = pSku.trim();
            console.log("cleanSku", cleanSku);
            const cleanCode = trimmedPart
              .replace(/\s*\(.*?\)\s*$/, "")
              .replace(/-\d+$/, "");
            const lookupKeys = [
              cleanCode,
              cleanCode.toLowerCase(),
              cleanCode.toUpperCase(),
              cleanCode.replace(/\s+/g, ""),
              cleanCode.replace(/\s+/g, "").toLowerCase(),
              cleanSku,
              cleanSku.toLowerCase(),
              cleanSku.toUpperCase(),
              cleanSku.replace(/\s+/g, ""),
              cleanSku.replace(/\s+/g, "").toLowerCase(),
            ];
            console.log("cleanCode", cleanCode);
            let catalogUnitPrice = 0;
            let catalogVat = i.vat ?? i.taxRate ?? "-";
            let catalogName = "";
            for (const k of lookupKeys) {
              if (k && productsCatalogMap[k]) {
                const info = productsCatalogMap[k];
                if (info.originalPrice > 0 || info.price > 0) {
                  catalogUnitPrice =
                    info.originalPrice > 0 ? info.originalPrice : info.price;
                }
                if (info.vat !== undefined && info.vat !== null) {
                  catalogVat = info.vat;
                }
                if (info.name) {
                  catalogName = info.name;
                }
                if (catalogUnitPrice > 0 && catalogName) break;
              }
            }

            subItems.push({
              pName: catalogName || rawName,
              pSku: cleanSku,
              pLot,
              pHsd,
              pQty: finalSubQty,
              standaloneVal: catalogUnitPrice * finalSubQty,
              catalogUnitPrice,
              vat: catalogVat,
            });
          }
        });
      }
    }

    // Rule 2: Product code matches format like AV007-0010426-27042029
    const singleFormatRegex = /^[A-Za-z]{2}\d{3}-\w+-\d+$/;
    if (!isParsed && singleFormatRegex.test(itemCode)) {
      isParsed = true;
      const blocks = itemCode.split("-");
      const firstBlock = blocks[0].trim();
      const pSku =
        firstBlock.substring(0, 2) + "MTP03010" + firstBlock.slice(-3);
      const pLot = blocks[1].trim();
      const pHsd = blocks[2].trim();
      const finalSubQty = qty;

      // Look up in productsCatalogMap
      const cleanSku = pSku.trim();
      const lookupKeys = [
        itemCode,
        itemCode.toLowerCase(),
        itemCode.toUpperCase(),
        itemCode.replace(/\s+/g, ""),
        itemCode.replace(/\s+/g, "").toLowerCase(),
        cleanSku,
        cleanSku.toLowerCase(),
        cleanSku.toUpperCase(),
        cleanSku.replace(/\s+/g, ""),
        cleanSku.replace(/\s+/g, "").toLowerCase(),
      ];

      let catalogUnitPrice = 0;
      let catalogVat = i.vat ?? i.taxRate ?? "-";
      let catalogName = "";
      for (const k of lookupKeys) {
        if (k && productsCatalogMap[k]) {
          const info = productsCatalogMap[k];
          if (info.originalPrice > 0 || info.price > 0) {
            catalogUnitPrice =
              info.originalPrice > 0 ? info.originalPrice : info.price;
          }
          if (info.vat !== undefined && info.vat !== null) {
            catalogVat = info.vat;
          }
          if (info.name) {
            catalogName = info.name;
          }
          if (catalogUnitPrice > 0 && catalogName) break;
        }
      }

      parsedItems.push({
        name: catalogName || rawName,
        sku: pSku,
        lot: pLot,
        hsd: pHsd,
        imei: i.imei || "",
        barcode: i.barcode || "",
        unit: i.unit || "",
        category: i.categoryName || i.category || "",
        weight: w,
        originalPrice: origPrice * finalSubQty,
        salePrice: price * finalSubQty,
        displaySalePrice: displaySalePrice * finalSubQty,
        priceDiff: (origPrice - price) * finalSubQty,
        qty: finalSubQty,
        discountPerProduct: qty > 0 ? disc / qty : 0,
        totalDiscount: disc,
        priceAfterDiscount: price * finalSubQty - disc,
        catalogUnitPrice,
        vat: catalogVat,
      });
    }

    // Default Fallback
    if (!isParsed) {
      const comboParts = rawName.split(/\s+và\s+/i);
      comboParts.forEach((part) => {
        const trimmedPart = part.trim();
        if (!trimmedPart) return;

        let pName = trimmedPart;
        let pSku = itemCode;
        let pLot = "";
        let pHsd = "";

        let pQty = 1;
        const parenMatch = trimmedPart.match(/\(\s*(\d+)/);
        if (parenMatch) {
          pQty = parseInt(parenMatch[1], 10);
        }

        const blocks = trimmedPart.split("-");
        if (blocks.length >= 3) {
          const firstBlock = blocks[0].trim();
          pSku = firstBlock.substring(0, 2) + "MTP03010" + firstBlock.slice(-3);
          pLot = blocks[1].trim();
          pHsd = blocks[2].trim();
        }

        const finalSubQty = pQty * (comboParts.length > 1 ? qty : 1);

        // Get standalone catalog price from productsCatalogMap
        const cleanSku = String(pSku || "").trim();
        const cleanName = String(pName || "").trim();

        const lookupKeys = [
          cleanSku,
          cleanSku.toLowerCase(),
          cleanSku.toUpperCase(),
          cleanSku.replace(/\s+/g, ""),
          cleanSku.replace(/\s+/g, "").toLowerCase(),
          cleanName,
          cleanName.toLowerCase(),
          cleanName.toUpperCase(),
          cleanName.replace(/\s+/g, ""),
          cleanName.replace(/\s+/g, "").toLowerCase(),
        ];

        let catalogUnitPrice = 0;
        let catalogVat = i.vat ?? i.taxRate ?? "";
        for (const k of lookupKeys) {
          if (k && productsCatalogMap[k]) {
            const info = productsCatalogMap[k];
            if (info.originalPrice > 0 || info.price > 0) {
              catalogUnitPrice =
                info.originalPrice > 0 ? info.originalPrice : info.price;
            }
            if (info.vat !== undefined && info.vat !== null) {
              catalogVat = info.vat;
            }
            if (catalogUnitPrice > 0) break;
          }
        }

        const standaloneVal = catalogUnitPrice * finalSubQty;

        subItems.push({
          pName,
          pSku,
          pLot,
          pHsd,
          pQty: finalSubQty,
          standaloneVal,
          catalogUnitPrice,
          vat: catalogVat,
        });
      });
    }

    if (subItems.length === 0) return;

    const totalComboSalePrice = price * qty;
    const totalComboDisplaySalePrice = displaySalePrice * qty;
    const totalComboOrigPrice = origPrice * qty;
    const totalComboDisc = disc;
    console.log("totalComboSalePrice", totalComboSalePrice);
    console.log("totalComboOrigPrice", totalComboOrigPrice);
    console.log("totalComboDisc", totalComboDisc);

    const sumStandaloneVal = subItems.reduce(
      (sum, sub) => sum + sub.standaloneVal,
      0,
    );

    subItems.forEach((sub) => {
      let ratio = 1 / subItems.length;
      if (sumStandaloneVal > 0) {
        ratio = sub.standaloneVal / sumStandaloneVal;
      }

      const itemSalePrice = Math.round(totalComboSalePrice * ratio);
      console.log("itemSalePrice", itemSalePrice);
      const itemDisplaySalePrice = Math.round(
        totalComboDisplaySalePrice * ratio,
      );
      const itemOrigPrice = Math.round(totalComboOrigPrice * ratio);
      const itemDisc = Math.round(totalComboDisc * ratio);
      const priceDiff = itemOrigPrice - itemSalePrice;
      const discountPerProduct = sub.pQty > 0 ? itemDisc / sub.pQty : 0;
      const priceAfterDiscount = itemSalePrice - itemDisc;

      parsedItems.push({
        name: sub.pName,
        sku: sub.pSku,
        lot: sub.pLot,
        hsd: sub.pHsd,
        imei: i.imei || "",
        barcode: i.barcode || "",
        unit: i.unit || "",
        category: i.categoryName || i.category || "",
        weight: w * sub.pQty,
        originalPrice: itemOrigPrice,
        salePrice: itemSalePrice,
        displaySalePrice: itemDisplaySalePrice,
        priceDiff,
        qty: sub.pQty,
        discountPerProduct,
        totalDiscount: itemDisc,
        priceAfterDiscount,
        catalogUnitPrice: sub.catalogUnitPrice,
        vat: sub.vat,
      });
    });
  });

  if (parsedItems.length === 0) {
    return [
      {
        name: "",
        sku: "",
        lot: "",
        hsd: "",
        imei: "",
        barcode: "",
        unit: "",
        category: "",
        weight: 0,
        originalPrice: 0,
        salePrice: 0,
        displaySalePrice: 0,
        priceDiff: 0,
        qty: 0,
        discountPerProduct: 0,
        totalDiscount: 0,
        priceAfterDiscount: 0,
      },
    ];
  }

  return parsedItems;
};

// Handle Export Excel with exact 88 specified columns
export const exportOrdersToExcel = ({
  orders,
  selectedKeys,
  productsCatalogMap,
  depotsMap,
  invoiceDataMap = {},
  triggerAlert,
}) => {
  if (selectedKeys.size === 0) {
    triggerAlert(
      "Vui lòng chọn ít nhất một đơn hàng để xuất Excel!",
      "warning",
    );
    return;
  }

  const targetOrders = orders.filter((o) => selectedKeys.has(o.id));

  if (targetOrders.length === 0) {
    triggerAlert("Không tìm thấy dữ liệu đơn hàng đã chọn!", "warning");
    return;
  }

  const headers = [
    "Ngày Xuất Hàng",
    "Loại chứng từ",
    "Số chứng từ",
    "Số Đơn Hàng",
    "Ngày đơn hàng",
    "Ông bà",
    "Địa chỉ",
    "Ghi chú",
    "ĐVTT",
    "Tỷ giá HT",
    "Tên Khách Hàng",
    "[Đối tượng công nợ] Tên KHNB",
    "Mã KH",
    "Đối tượng ghi có",
    "[Đối tượng ghi có] Tên KHNB",
    "[Đối tượng ghi có] Mã",
    "Chi tiết các khoản",
    "TK nợ",
    "TK có",
    "Tên Sản Phẩm",
    "[Vật tư hàng hóa] Đvt",
    "Mã Sản Phẩm",
    "Đơn Vị Lẻ",
    "Kho hàng",
    "Kho nhận",
    "Số Lot",
    "Ngày Hết Hạn",
    "Số lượng Lẻ",
    "Hesoquydoi",
    "Số lượng",
    "Đơn giá VND",
    "Tiền Ngtệ",
    "Doanh Số",
    "Thuế suất",
    "TK nợ VAT",
    "Tk có VAT",
    "Thành Tiền Thuế",
    "% Chiết Khấu",
    "TK Nợ CK",
    "TK Có CK",
    "Tổng Tiền CK",
    "Mẫu H.đơn",
    "Seri",
    "Ngày hóa đơn",
    "Số hóa đơn",
    "Nhóm VAT",
    "MS thuế",
    "KH thuế",
    "ĐC thuế",
    "Hình Thức Thanh Toán",
    "Bộ phận",
    "GVTC",
    "TK GVHB",
    "TK hạch toán",
    "Giá vốn",
    "TSCĐ",
    "[TSCĐ] Mã",
    "Tên Kênh KH",
    "Chương trình",
    "[Chương trình] Mã",
    "Khoản mục Nội bộ",
    "[Khoản mục Nội bộ] Mã",
    "Hop dong",
    "Số phiếu MTV",
    "Vùng",
    "Đơn giá (đvt)",
    "Dòng tiền",
    "[Dòng tiền] Mã",
    "Khoản mục Thuế",
    "Tên Nhân Viên",
    "Mảng kinh doanh",
    "Mã NV (10 ký tự)",
    "Ngân hàng",
    "Kèm theo . . . . . .",
    "Số tiền 2 Ngtệ",
    "Kênh KH",
    "Mức CK T.Toán",
    "Phân xưởng",
    "[Phân xưởng] Mã",
    "ĐTCP_TP",
    "[ĐTCP_TP] Mã",
    "Số lô SP",
    "SLSX",
    "Mã Kho",
    "[Kho nhận] Mã",
    "[Mảng kinh doanh] Mã",
    "Ngày hạn t.t",
    "Mã loại điểm bán",
  ];

  const buildSheetData = (isTC) => {
    return [
      ["BẢNG KÊ CHI TIẾT ĐƠN HÀNG NHANH.VN"],
      [`Ngày xuất file: ${dayjs().format("DD/MM/YYYY")}`],
      [],
      headers,
      ...targetOrders.flatMap((o) => {
        const raw = o.raw || {};
        const info = raw.info || {};
        const channel = raw.channel || {};
        const carrier = raw.carrier || {};
        const prodItems = parseOrderProducts(raw, productsCatalogMap);

        const orderIdStr = info.id || raw.id || o.id || "";
        const shopOrderIdStr =
          channel.appOrderId ||
          raw.appOrderId ||
          o.shopOrderId ||
          o.appOrderId ||
          orderIdStr;
        const createdDateStr = formatTimestamp(info.createdAt || raw.createdAt);
        const dateYYMMDD = formatTimestampToYYMMDD(
          info.createdAt || raw.createdAt,
        );
        const trackingCodeStr =
          carrier.carrierCode ||
          carrier.code ||
          raw.trackingCode ||
          carrier.trackingCode ||
          info.carrierCode ||
          raw.carrierCode ||
          "";
        const soChungTu =
          dateYYMMDD && trackingCodeStr
            ? `${dateYYMMDD}-${trackingCodeStr}`
            : "";

        const invoiceInfo = invoiceDataMap[o.id] || {};
        const ngayHoaDon = invoiceInfo.invoiceDate || "";
        const soHoaDon = invoiceInfo.invoiceNumber || "";
        const ngayXuatHang = ngayHoaDon ? ngayHoaDon : "";

        const channelStr = String(
          getSaleChannelName(channel.saleChannel ?? raw.saleChannel) || "",
        ).toLowerCase();
        let maKH = "AVMKH033251";
        let maLoaiDiemBan = "LDB_1901";
        let kenhKH = "KBH_012";
        if (
          channelStr.includes("shoppe") ||
          channelStr.includes("shopee") ||
          channel.saleChannel === 42 ||
          raw.saleChannel === 42
        ) {
          maKH = "AVMKH033249";
          maLoaiDiemBan = "LDB_2001";
          kenhKH = "KBH_012";
        } else if (
          channelStr.includes("tiktok") ||
          channel.saleChannel === 48 ||
          raw.saleChannel === 48
        ) {
          maKH = "AVMKH033251";
          maLoaiDiemBan = "LDB_1901";
          kenhKH = "KBH_014";
        }

        const costVal = Number(raw.costPrice ?? raw.capitalPrice ?? 0);

        return prodItems.map((prod) => {
          const tkNoCK = isTC ? "" : prod.totalDiscount > 0 ? "64111" : "";
          const tkCoCK = isTC ? "" : prod.totalDiscount > 0 ? "1311" : "";
          const totalDiscountVal = isTC ? "" : prod.totalDiscount;

          // Map SKU exported in Excel back to productsCatalogMap to fetch catalog original price & vat
          const cleanSku = String(prod.sku || "").trim();
          const cleanName = String(prod.name || "").trim();
          const lookupKeys = [
            cleanSku,
            cleanSku.toLowerCase(),
            cleanSku.toUpperCase(),
            cleanSku.replace(/\s+/g, ""),
            cleanSku.replace(/\s+/g, "").toLowerCase(),
            cleanName,
            cleanName.toLowerCase(),
            cleanName.toUpperCase(),
            cleanName.replace(/\s+/g, ""),
            cleanName.replace(/\s+/g, "").toLowerCase(),
          ];

          let catalogUnitPrice = 0;
          let catalogVat = prod.vat !== undefined ? prod.vat : "";
          for (const k of lookupKeys) {
            if (k && productsCatalogMap[k]) {
              const infoItem = productsCatalogMap[k];
              if (infoItem.originalPrice > 0 || infoItem.price > 0) {
                catalogUnitPrice =
                  infoItem.originalPrice > 0
                    ? infoItem.originalPrice
                    : infoItem.price;
              }
              if (infoItem.vat !== undefined && infoItem.vat !== null) {
                catalogVat = infoItem.vat;
              }
              if (catalogUnitPrice > 0) break;
            }
          }
          const baseSalePrice =
            prod.displaySalePrice !== undefined &&
            prod.displaySalePrice !== null
              ? prod.displaySalePrice
              : prod.salePrice || 0;

          const vatNum = Number(catalogVat);
          const isVatValid = !Number.isNaN(vatNum) && vatNum > 0;

          const priceBeforeVat = isVatValid
            ? baseSalePrice / (1 + vatNum / 100)
            : baseSalePrice;

          const discountBeforeVat = isVatValid
            ? prod.totalDiscount / (1 + vatNum / 100)
            : prod.totalDiscount;

          const salePriceVal = isTC
            ? Math.round(priceBeforeVat - discountBeforeVat)
            : Math.round(priceBeforeVat);

          const unitPriceBeforeVat =
            prod.qty > 0
              ? Math.round(priceBeforeVat / prod.qty)
              : Math.round(priceBeforeVat);

          const khoanMucNoiBoMa =
            prod.totalDiscount > 0 ||
            baseSalePrice === 0 ||
            unitPriceBeforeVat === 0
              ? "KM0101"
              : "";

          const thanhTienThue = isVatValid
            ? Math.round((salePriceVal * vatNum) / 100)
            : 0;

          const isHKM = baseSalePrice === 0 || unitPriceBeforeVat === 0;

          return [
            ngayXuatHang, // 1. Ngày Xuất Hàng
            "", // 2. Loại chứng từ
            soChungTu, // 3. Số chứng từ
            shopOrderIdStr, // 4. Số Đơn Hàng
            createdDateStr, // 5. Ngày đơn hàng
            "", // 6. Ông bà
            "", // 7. Địa chỉ
            trackingCodeStr, // 8. Ghi chú
            "VNĐ", // 9. ĐVTT
            "", // 10. Tỷ giá HT
            "", // 11. Tên Khách Hàng
            "", // 12. [Đối tượng công nợ] Tên KHNB
            maKH, // 13. Mã KH
            "", // 14. Đối tượng ghi có
            "", // 15. [Đối tượng ghi có] Tên KHNB
            "", // 16. [Đối tượng ghi có] Mã
            "", // 17. Chi tiết các khoản
            isTC ? "1311" : isHKM ? "" : "1311", // 18. TK nợ
            isTC ? "51121" : isHKM ? "" : "51121", // 19. TK có
            "", // 20. Tên Sản Phẩm
            "", // 21. [Vật tư hàng hóa] Đvt
            prod.sku, // 22. Mã Sản Phẩm
            "", // 23. Đơn Vị Lẻ
            "", // 24. Kho hàng
            "", // 25. Kho nhận
            prod.lot, // 26. Số Lot
            formatHsd(prod.hsd), // 27. Ngày Hết Hạn
            prod.qty, // 28. Số lượng Lẻ
            "", // 29. Hesoquydoi
            prod.qty, // 30. Số lượng
            unitPriceBeforeVat, // 31. Đơn giá VND
            "", // 32. Tiền Ngtệ
            salePriceVal, // 33. Doanh Số
            catalogVat !== "" && catalogVat !== undefined && catalogVat !== null
              ? catalogVat
              : "", // 34. Thuế suất
            isTC ? "1311" : isHKM ? "" : "1311", // 35. TK nợ VAT
            isTC ? "333111" : isHKM ? "" : "333111", // 36. Tk có VAT
            thanhTienThue, // 37. Thành Tiền Thuế
            "", // 38. % Chiết Khấu
            tkNoCK, // 39. TK Nợ CK
            tkCoCK, // 40. TK Có CK
            totalDiscountVal, // 41. Tổng Tiền CK
            "", // 42. Mẫu H.đơn
            "", // 43. Seri
            ngayHoaDon, // 44. Ngày hóa đơn
            soHoaDon, // 45. Số hóa đơn
            "", // 46. Nhóm VAT
            "", // 47. MS thuế
            "", // 48. KH thuế
            "", // 49. ĐC thuế
            "", // 50. Hình Thức Thanh Toán
            "", // 51. Bộ phận
            "", // 52. GVTC
            isTC ? "6321" : isHKM ? "64111" : "6321", // 53. TK GVHB
            "155", // 54. TK hạch toán
            costVal, // 55. Giá vốn
            "", // 56. TSCĐ
            "", // 57. [TSCĐ] Mã
            "", // 58. Tên Kênh KH
            "", // 59. Chương trình
            "", // 60. [Chương trình] Mã
            "", // 61. Khoản mục Nội bộ
            khoanMucNoiBoMa, // 62. [Khoản mục Nội bộ] Mã
            "", // 63. Hop dong
            "", // 64. Số phiếu MTV
            "", // 65. Vùng
            "", // 66. Đơn giá (đvt)
            "", // 67. Dòng tiền
            "", // 68. [Dòng tiền] Mã
            "", // 69. Khoản mục Thuế
            "", // 70. Tên Nhân Viên
            "", // 71. Mảng kinh doanh
            "", // 72. Mã NV (10 ký tự)
            "", // 73. Ngân hàng
            "", // 74. Kèm theo . . . . . .
            "", // 75. Số tiền 2 Ngtệ
            kenhKH, // 76. Kênh KH
            "", // 77. Mức CK T.Toán
            "", // 78. Phân xưởng
            "", // 79. [Phân xưởng] Mã
            "", // 80. ĐTCP_TP
            "", // 81. [ĐTCP_TP] Mã
            "", // 82. Số lô SP
            "", // 83. SLSX
            "AVMKK1407", // 84. Mã Kho
            "", // 85. [Kho nhận] Mã
            "AVMMKD0004", // 86. [Mảng kinh doanh] Mã
            "", // 87. Ngày hạn t.t
            "", // 88. Mã loại điểm bán
          ];
        });
      }),
    ];
  };

  const wb = XLSX.utils.book_new();

  const wsNB = XLSX.utils.aoa_to_sheet(buildSheetData(false));
  XLSX.utils.book_append_sheet(wb, wsNB, "Import NB");

  const wsTC = XLSX.utils.aoa_to_sheet(buildSheetData(true));
  XLSX.utils.book_append_sheet(wb, wsTC, "Import TC");

  // Apply column widths and header merges
  const applyStyles = (ws, data) => {
    const maxColWidths = headers.map((_, colIdx) => {
      const maxLen = data.reduce((max, row) => {
        const cell = row[colIdx];
        const len = cell ? cell.toString().length : 0;
        return Math.max(max, len);
      }, 0);
      return { wch: Math.min(Math.max(maxLen + 3, 10), 40) };
    });
    ws["!cols"] = maxColWidths;
    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: headers.length - 1 } },
    ];
  };

  applyStyles(wsNB, buildSheetData(false));
  applyStyles(wsTC, buildSheetData(true));

  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  saveAs(blob, `DonHang_Nhanh_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`);
  triggerAlert(
    `Đã xuất Excel thành công ${targetOrders.length} đơn hàng với 2 sheet Import NB và Import TC!`,
  );
};
